// src/components/JoinHubButton.jsx
"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { requireSignedIn } from "@/lib/authRedirect";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "@/context/PortalContext";
import LoadingState from "@/components/LoadingState";

/**
 * ENV:
 *  - NEXT_PUBLIC_CF_JOIN_HUB  = https://<region>-<project>.cloudfunctions.net/nona-joinhub
 *  - NEXT_PUBLIC_CF_LEAVE_HUB = https://<region>-<project>.cloudfunctions.net/nona-leavehub
 */
const JOIN_URL  = process.env.NEXT_PUBLIC_CF_JOIN_HUB;
const LEAVE_URL = process.env.NEXT_PUBLIC_CF_LEAVE_HUB;

export default function JoinHubButton({
  hubSlug,
  ownerUid = "",
  member = null,
  membershipLoading = false,
  onMembershipChange,
}) {
  const router = useRouter();
  const portal = usePortal();
  const { user, loading: authLoading } = useAuth();
  // loading | not | pending | active | banned | owner
  const [status, setStatus] = useState("loading");
  const [err, setErr] = useState("");

  const withIdToken = async () => {
    if (!user) throw new Error("Sign in to continue.");
    const token = await user.getIdToken();
    return `Bearer ${token}`;
  };

  useEffect(() => {
    setErr("");
    if (authLoading || membershipLoading) {
      setStatus("loading");
      return;
    }
    if (!hubSlug || !user) {
      setStatus("not");
      return;
    }
    if (ownerUid === user.uid) {
      setStatus("owner");
      return;
    }

    const next = member?.status ?? (member ? "active" : "not");
    setStatus(["active", "pending", "banned"].includes(next) ? next : "not");
  }, [authLoading, hubSlug, member, membershipLoading, ownerUid, user]);

  const join = async () => {
    setErr("");
    try {
      if (!requireSignedIn(user, router, { intent: "join-space" })) return;
      const portalSpace = portal?.spaces?.find((space) => space.hubSlug === hubSlug);
      const usePortalJoin = portalSpace?.membershipBehavior === "auto-join-on-first-interaction";
      if (!usePortalJoin && !JOIN_URL) throw new Error("Join endpoint is not configured.");
      setStatus("loading");
      const authHeader = await withIdToken();
      const r = await fetch(usePortalJoin ? `/api/portal/spaces/${encodeURIComponent(hubSlug)}/membership` : JOIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": authHeader },
        body: JSON.stringify(usePortalJoin ? { action: "join" } : { hubSlug }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || "Could not join.");
      const nextStatus = j.state === "active" ? "active" :
        j.state === "pending" ? "pending" :
        j.state === "banned" ? "banned" : "not";
      setStatus(nextStatus);
      onMembershipChange?.(
        nextStatus === "not"
          ? null
          : { ...(member || {}), hubId: hubSlug, uid: user.uid, status: nextStatus }
      );
    } catch (e) {
      setErr(e.message || "Could not join.");
      setStatus("not");
    }
  };

  const leave = async () => {
    setErr("");
    try {
      if (!requireSignedIn(user, router, { intent: "leave-space" })) return;
      if (!LEAVE_URL) throw new Error("Leave endpoint is not configured.");
      setStatus("loading");
      const authHeader = await withIdToken();
      const r = await fetch(LEAVE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": authHeader },
        body: JSON.stringify({ hubSlug }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || "Could not leave.");
      setStatus("not");
      onMembershipChange?.(null);
    } catch (e) {
      setErr(e.message || "Could not leave.");
      // If leave fails (e.g., owner), reflect still active
      if (e.message?.toLowerCase().includes("owner")) setStatus("active");
      else setStatus("not");
    }
  };

  if (status === "loading") {
    return (
      <button className="btn min-w-24" disabled aria-busy="true">
        <LoadingState variant="inline" message="Checking..." />
      </button>
    );
  }

  // Owner: no join/leave; show a small chip
  if (status === "owner") {
    return (
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 text-xs rounded-[var(--radius)] border border-[var(--border)]">
          Owner
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {status === "active" && (
        <button className="btn" onClick={leave} aria-label="Leave space">Leave</button>
      )}

      {status === "pending" && (
        <button className="btn opacity-60 cursor-not-allowed" disabled title="Request sent">
          Requested
        </button>
      )}

      {status === "not" && (
        <button className="btn" onClick={join} aria-label="Join space">Join</button>
      )}

      {status === "banned" && (
        <span className="text-xs text-red-500">You no longer have access.</span>
      )}

      {err && <span className="text-xs text-red-500">{err}</span>}
    </div>
  );
}
